import React, { useEffect } from 'react'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { Map, List } from 'immutable'
import { connect as connectStaticText } from '../../StaticText/connect'
import { getBoundActions } from '../../../actions/index'
import GaiaLogo, { TYPE_TEAL } from '../../GaiaLogo/index'
import { getPrimary } from '../../../services/languages/index'
import { ACCOUNT_TYPE_ZUORA } from '../../../services/user-account/index'
import { BILLING_PLAN_GRID_LOCATION_CHECKOUT } from '../../../services/plans/utils'
import { getUserLastEntitlement } from '../../../services/winback/index'
import { useGetPlanGridData } from '../../../services/plans/hooks'

import CartSinglePagePreviousPlan from './CartSinglePagePreviousPlan'
import CartSinglePagePaymentSection from './CartSinglePagePaymentSection'
import CartSinglePageSelectPlan from './CartSinglePageSelectPlan'
import { hasPaymentData } from '../utils'

/**
 * Component to handle the winback
 * @param {Map} props.staticText - Static text translations
 * @param {Map} props.auth - Auth state
 * @param {Map} props.paytrack - Paytrack state
 * @param {String} props.userLanguage - User language
 * @param {Function} props.setRenderWinback - Function to set render winback
 * @param {Function} props.setRenderProcessing - Function to set render processing
 * @param {Function} props.setCheckoutAccountValid - Function to set checkout account valid
 * @param {Function} props.setCheckoutWinbackUseDefaultPayment
 * @returns {React.ReactNode}
 */
function CartSinglePageWinback (props) {
  const {
    staticText,
    auth,
    paytrack,
    userLanguage,
    setRenderWinback,
    setRenderProcessing,
    setCheckoutAccountValid,
    setCheckoutWinbackUseDefaultPayment,
  } = props
  const authJwt = auth.get('jwt')
  const lastEntitlement = getUserLastEntitlement(paytrack)
  const plans = useGetPlanGridData({
    userLanguage,
    authJwt,
    page: BILLING_PLAN_GRID_LOCATION_CHECKOUT,
  }) || List()
  const previousPlan = plans.find((plan) => plan.get('entitlementContentSet') === lastEntitlement) || null

  // Set account as valid on mount
  useEffect(() => {
    setCheckoutAccountValid(true)
  }, [])

  // Set loading state when previous plan data is ready
  useEffect(() => {
    if (Map.isMap(previousPlan) && previousPlan.size > 0) {
      const userHasPaymentData = hasPaymentData({ ...props, selectedPlan: previousPlan })
      setCheckoutWinbackUseDefaultPayment(userHasPaymentData)
      setRenderProcessing(false)
    }
  }, [previousPlan])

  // Early return if no previous plan data
  if (!Map.isMap(previousPlan)) {
    return null
  }

  return (
    <div className="winback">
      <div className="winback__background">
        <div className="winback__overlay"></div>
      </div>
      <div className="winback__container">
        <GaiaLogo
          type={TYPE_TEAL}
          className={['winback__logo']}
          isHref={false}
        />
        <div className="winback__content">
          <div className="winback__title">
            {staticText.getIn(['data', 'restartPlan'])}
          </div>
          {/* Display previous plan information */}
          <CartSinglePagePreviousPlan
            staticText={staticText}
            previousPlan={previousPlan}
          />
          {/* Handle payment setup and subscription restart */}
          <CartSinglePagePaymentSection
            {...props}
            selectedPlan={previousPlan}
          />
          {/* Allow user to select a different plan */}
          <CartSinglePageSelectPlan
            staticText={staticText}
            selectedPlan={previousPlan}
            setRenderWinback={setRenderWinback}
          />
        </div>
      </div>
    </div>
  )
}

export default compose(
  connectStaticText({ storeKey: 'CartSinglePage' }),
  connectRedux(
    (state) => {
      const {
        auth,
        user,
        userAccount,
        checkout,
        resolver,
        paytrack,
        plans,
        velocityCheck,
        app,
        inboundTracking,
        payment,
      } = state
      const isZuora = userAccount.get('accountType') === ACCOUNT_TYPE_ZUORA
      const userLanguage = getPrimary(user.getIn(['data', 'language']))
      const paymentMethod = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paymentMethod'])
      const billingAddress = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAddress'])

      return {
        auth,
        user,
        userAccount,
        resolver,
        paytrack,
        plans: plans.getIn(['data', 'plans']) || null,
        checkout,
        velocityCheck,
        app,
        isZuora,
        userLanguage,
        paymentMethod,
        billingAddress,
        inboundTracking,
        payment,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        getUserEmailAvailability: actions.user.getUserEmailAvailability,
        resetUserEmailAvailability: actions.user.resetUserEmailAvailability,
        resetUserChildAccount: actions.user.resetUserChildAccount,
        setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
        updateUser: actions.user.updateUser,
        getUserContactData: actions.user.getUserContactData,
        dismissModal: actions.dialog.dismissModal,
        setCheckoutSinglePageStep: actions.checkout.setCheckoutSinglePageStep,
        setCheckoutAccountValid: actions.checkout.setCheckoutAccountValid,
        getAdyenPaymentAuthorization: actions.adyen.getAdyenPaymentAuthorization,
        initAdyen: actions.adyen.initAdyen,
        resetAdyen: actions.adyen.resetAdyen,
        resetPaymentMethods: actions.payment.resetPaymentMethods,
        processCheckoutOrder: actions.checkout.processCheckoutOrder,
        setCheckoutWinbackUseDefaultPayment: actions.checkout.setCheckoutWinbackUseDefaultPayment,
        setCheckoutWinbackStep: actions.checkout.setCheckoutWinbackStep,
        setEventDefault: actions.eventTracking.setEventDefault,
        sendOptimizelyFeatureEvent: actions.optimizelyFeature.sendOptimizelyFeatureEvent,
        setPlansSelection: actions.plans.setPlansSelection,
      }
    },
  ),
)(CartSinglePageWinback)
